import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Package, ShoppingCart, Shield, FileText, Plus, ArrowRight, RefreshCw,
  Loader2, DollarSign, TrendingUp, Clock, CheckCircle2, AlertTriangle,
  Briefcase, Wrench, FileCheck, BarChart3
} from 'lucide-react';
import { productsAPI, salesQuotesAPI } from '../api';
import api from '../api';

const productTypeColors = {
  product: 'bg-blue-100 text-blue-800',
  service: 'bg-purple-100 text-purple-800',
  sla: 'bg-green-100 text-green-800',
  support_package: 'bg-orange-100 text-orange-800',
  subscription: 'bg-pink-100 text-pink-800',
  pump: 'bg-cyan-100 text-cyan-800',
  part: 'bg-slate-100 text-slate-800',
  accessory: 'bg-amber-100 text-amber-800',
  other: 'bg-gray-100 text-gray-800'
};

const quoteStatusColors = {
  draft: 'bg-slate-100 text-slate-800',
  pending: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-blue-100 text-blue-800',
  viewed: 'bg-purple-100 text-purple-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  expired: 'bg-gray-100 text-gray-800',
  converted: 'bg-emerald-100 text-emerald-800'
};

export const CommerceOverviewPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data state
  const [products, setProducts] = useState([]);
  const [productStats, setProductStats] = useState({
    total: 0,
    products: 0,
    services: 0,
    slas: 0,
    supportPackages: 0
  });
  const [quotesDashboard, setQuotesDashboard] = useState(null);
  const [warranties, setWarranties] = useState([]);
  const [warrantyStats, setWarrantyStats] = useState({ total: 0, active: 0, expiring: 0 });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setRefreshing(true);
      const [productsRes, quotesRes, warrantiesRes] = await Promise.all([
        productsAPI.getAll(),
        salesQuotesAPI.getDashboard().catch(() => ({ data: null })),
        api.get('/warranties').catch(() => ({ data: [] }))
      ]);
      
      // Products
      const prods = productsRes.data || [];
      setProducts(prods);
      
      // Calculate product type stats
      const stats = {
        total: prods.length,
        products: prods.filter(p => ['product', 'pump', 'part', 'accessory'].includes(p.product_type)).length,
        services: prods.filter(p => p.product_type === 'service').length,
        slas: prods.filter(p => p.product_type === 'sla').length,
        supportPackages: prods.filter(p => p.product_type === 'support_package').length,
        subscriptions: prods.filter(p => p.product_type === 'subscription').length
      };
      setProductStats(stats);
      
      // Quotes
      setQuotesDashboard(quotesRes.data);
      
      // Warranties
      const wars = warrantiesRes.data || [];
      setWarranties(wars);
      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      setWarrantyStats({
        total: wars.length,
        active: wars.filter(w => new Date(w.expiry_date) > now).length,
        expiring: wars.filter(w => {
          const exp = new Date(w.expiry_date);
          return exp > now && exp < thirtyDaysFromNow;
        }).length
      });
      
    } catch (error) {
      console.error('Failed to fetch commerce data:', error);
      toast.error('Failed to load commerce overview');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="commerce-overview">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingCart className="h-6 w-6" />
            Commerce Overview
          </h1>
          <p className="text-muted-foreground">
            Manage products, services, quotes, and warranties
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchAllData} disabled={refreshing}>
            {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
          <Link to="/commerce/products">
            <Button variant="outline" size="sm">
              <Package className="h-4 w-4 mr-2" />
              Products
            </Button>
          </Link>
          <Link to="/commerce/quotes/new">
            <Button data-testid="new-quote-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Quote
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Products</p>
                <p className="text-3xl font-bold">{productStats.total}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Services & SLAs</p>
                <p className="text-3xl font-bold text-purple-600">
                  {productStats.services + productStats.slas + productStats.supportPackages}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <Briefcase className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Open Quotes</p>
                <p className="text-3xl font-bold text-amber-600">
                  {quotesDashboard?.stats?.sent || 0}
                </p>
              </div>
              <div className="p-3 bg-amber-100 rounded-lg">
                <FileText className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Warranties</p>
                <p className="text-3xl font-bold text-green-600">{warrantyStats.active}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Quote Value</p>
                <p className="text-3xl font-bold text-emerald-600">
                  ${(quotesDashboard?.stats?.total_accepted_value || 0).toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Products & Services Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="h-5 w-5" />
              Products & Services
            </CardTitle>
            <CardDescription>Catalog breakdown by type</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-sm">Physical Products</span>
                </div>
                <span className="font-medium">{productStats.products}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                  <span className="text-sm">Services</span>
                </div>
                <span className="font-medium">{productStats.services}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-sm">SLAs</span>
                </div>
                <span className="font-medium">{productStats.slas}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                  <span className="text-sm">Support Packages</span>
                </div>
                <span className="font-medium">{productStats.supportPackages}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-pink-500"></div>
                  <span className="text-sm">Subscriptions</span>
                </div>
                <span className="font-medium">{productStats.subscriptions || 0}</span>
              </div>
            </div>
            
            <Link to="/commerce/products" className="block">
              <Button variant="outline" size="sm" className="w-full">
                Manage Products
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Quote Pipeline */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Quote Pipeline
            </CardTitle>
            <CardDescription>Sales quotes by status</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {quotesDashboard?.stats ? (
              <>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Draft</span>
                    <Badge variant="secondary">{quotesDashboard.stats.draft}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Sent</span>
                    <Badge className="bg-blue-100 text-blue-800">{quotesDashboard.stats.sent}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Accepted</span>
                    <Badge className="bg-green-100 text-green-800">{quotesDashboard.stats.accepted}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Converted to Orders</span>
                    <Badge className="bg-emerald-100 text-emerald-800">{quotesDashboard.stats.converted}</Badge>
                  </div>
                </div>
                
                {quotesDashboard.stats.expiring_soon > 0 && (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <span className="text-sm text-amber-700">
                      {quotesDashboard.stats.expiring_soon} quotes expiring soon
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No quotes yet</p>
              </div>
            )}
            
            <Link to="/commerce/quotes" className="block">
              <Button variant="outline" size="sm" className="w-full">
                View All Quotes
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Warranty Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Warranty Status
            </CardTitle>
            <CardDescription>Active warranties overview</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total Registered</span>
                <span className="font-medium">{warrantyStats.total}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Active</span>
                <Badge className="bg-green-100 text-green-800">{warrantyStats.active}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Expiring (30 days)</span>
                <Badge className={warrantyStats.expiring > 0 ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-800"}>
                  {warrantyStats.expiring}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Expired</span>
                <Badge variant="secondary">{warrantyStats.total - warrantyStats.active}</Badge>
              </div>
            </div>
            
            {warrantyStats.expiring > 0 && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <Clock className="h-4 w-4 text-amber-600" />
                <span className="text-sm text-amber-700">
                  {warrantyStats.expiring} warranties expiring soon
                </span>
              </div>
            )}
            
            <Link to="/commerce/warranties" className="block">
              <Button variant="outline" size="sm" className="w-full">
                Manage Warranties
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Quotes */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Recent Quotes</CardTitle>
              <CardDescription>Latest sales quotes</CardDescription>
            </div>
            <Link to="/commerce/quotes">
              <Button variant="outline" size="sm">
                View All
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {quotesDashboard?.recent_quotes?.length > 0 ? (
              <ScrollArea className="h-[250px]">
                <div className="space-y-3">
                  {quotesDashboard.recent_quotes.map((quote) => (
                    <Link 
                      key={quote.id} 
                      to={`/commerce/quotes/${quote.id}`}
                      className="block"
                    >
                      <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <FileText className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{quote.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {quote.quote_number} • {quote.contact_name || quote.company_name || 'No contact'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={quoteStatusColors[quote.status] || 'bg-gray-100 text-gray-800'}>
                            {quote.status}
                          </Badge>
                          <span className="text-sm font-medium">${quote.total?.toLocaleString()}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No quotes yet</p>
                <Link to="/commerce/quotes/new">
                  <Button variant="outline" size="sm" className="mt-2">
                    Create First Quote
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Products */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Products Catalog</CardTitle>
              <CardDescription>Recently added products</CardDescription>
            </div>
            <Link to="/commerce/products">
              <Button variant="outline" size="sm">
                View All
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {products.length > 0 ? (
              <ScrollArea className="h-[250px]">
                <div className="space-y-3">
                  {products.slice(0, 5).map((product) => (
                    <div 
                      key={product.id} 
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-100 rounded-lg">
                          {product.product_type === 'service' ? (
                            <Briefcase className="h-4 w-4 text-purple-600" />
                          ) : product.product_type === 'sla' ? (
                            <FileCheck className="h-4 w-4 text-green-600" />
                          ) : product.product_type === 'support_package' ? (
                            <Wrench className="h-4 w-4 text-orange-600" />
                          ) : (
                            <Package className="h-4 w-4 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{product.name}</p>
                          <p className="text-xs text-muted-foreground">
                            SKU: {product.sku}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={productTypeColors[product.product_type] || 'bg-gray-100 text-gray-800'}>
                          {product.product_type?.replace('_', ' ')}
                        </Badge>
                        <span className="text-sm font-medium">${product.price?.toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No products yet</p>
                <Link to="/commerce/products">
                  <Button variant="outline" size="sm" className="mt-2">
                    Add First Product
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/commerce/products">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6 text-center">
              <Package className="h-8 w-8 mx-auto mb-2 text-blue-600" />
              <h3 className="font-semibold">Products & Services</h3>
              <p className="text-sm text-muted-foreground">Manage your catalog</p>
            </CardContent>
          </Card>
        </Link>
        
        <Link to="/commerce/quotes">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6 text-center">
              <FileText className="h-8 w-8 mx-auto mb-2 text-amber-600" />
              <h3 className="font-semibold">Sales Quotes</h3>
              <p className="text-sm text-muted-foreground">Create & manage quotes</p>
            </CardContent>
          </Card>
        </Link>
        
        <Link to="/commerce/warranties">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6 text-center">
              <Shield className="h-8 w-8 mx-auto mb-2 text-green-600" />
              <h3 className="font-semibold">Warranties</h3>
              <p className="text-sm text-muted-foreground">Track warranty status</p>
            </CardContent>
          </Card>
        </Link>
        
        <Link to="/commerce/documentation">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6 text-center">
              <FileCheck className="h-8 w-8 mx-auto mb-2 text-purple-600" />
              <h3 className="font-semibold">Documentation</h3>
              <p className="text-sm text-muted-foreground">Product documents</p>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default CommerceOverviewPage;
